import { normalizeDriverImage } from "./driver-media.js";
import { captureDriverLocation } from "./location.js";
import {
  DRIVER_CAMERA_STAMP_VERSION,
  renderDriverMiniMap,
  resolveDriverCaptureTime,
  reverseGeocodeDriverLocation,
  stampDriverEvidence,
} from "./driver-camera-stamp.js";

function cleanMetadata(metadata = {}) {
  const driverName = String(metadata.driverName || "").trim();
  if (!driverName) {
    throw new Error("Identitas driver belum tersedia. Muat ulang Sedulur Dalan lalu ambil foto kembali.");
  }
  return {
    driverName,
    fleetOrderNumber: String(metadata.fleetOrderNumber || "").trim() || null,
    stopName: String(metadata.stopName || "").trim() || null,
    stopSequence: Number(metadata.stopSequence || 0) || null,
    stopTotal: Number(metadata.stopTotal || 0) || null,
    vehiclePlate: String(metadata.vehiclePlate || "").trim() || null,
  };
}

export async function prepareDriverCameraEvidence(file, {
  sourceType = "CAMERA",
  label = "Foto operasional",
  mediaKind = "PHOTO",
  metadata = {},
} = {}) {
  const context = cleanMetadata(metadata);
  const normalized = await normalizeDriverImage(file);
  const capturedAtDevice = resolveDriverCaptureTime(file, sourceType);
  const locationResult = await captureDriverLocation({ timeoutMs: 12_000, maximumAgeMs: 30_000 });
  const location = locationResult.location || {};
  const geocoding = await reverseGeocodeDriverLocation(location);
  const miniMap = await renderDriverMiniMap(location);
  const stampContext = {
    capturedAt: capturedAtDevice,
    addressText: geocoding.addressText,
    location,
    driverName: context.driverName,
    activityLabel: String(metadata.activityLabel || label || mediaKind).trim(),
    fleetOrderNumber: context.fleetOrderNumber,
    stopName: context.stopName,
    stopSequence: context.stopSequence,
    stopTotal: context.stopTotal,
    vehiclePlate: context.vehiclePlate,
    sourceType,
  };
  const stamped = await stampDriverEvidence(normalized.file, stampContext, miniMap);
  miniMap.canvas = null;

  return {
    file: stamped.file,
    originalFile: normalized.file,
    width: stamped.width,
    height: stamped.height,
    originalWidth: normalized.width,
    originalHeight: normalized.height,
    compressed: normalized.compressed || stamped.file.size < normalized.file.size,
    stampMetadata: {
      stampVersion: DRIVER_CAMERA_STAMP_VERSION,
      stamped: true,
      sourceType: String(sourceType || "CAMERA").toUpperCase(),
      capturedAtDevice,
      capturedAtJakarta: new Intl.DateTimeFormat("sv-SE", {
        dateStyle: "short",
        timeStyle: "medium",
        hour12: false,
        timeZone: "Asia/Jakarta",
      }).format(new Date(capturedAtDevice)),
      driverName: context.driverName,
      activityLabel: stampContext.activityLabel,
      fleetOrderNumber: context.fleetOrderNumber,
      stopName: context.stopName,
      stopSequence: context.stopSequence,
      stopTotal: context.stopTotal,
      vehiclePlate: context.vehiclePlate,
      latitude: Number.isFinite(Number(location.latitude)) ? Number(location.latitude) : null,
      longitude: Number.isFinite(Number(location.longitude)) ? Number(location.longitude) : null,
      gpsAccuracyM: Number.isFinite(Number(location.accuracyM)) ? Number(location.accuracyM) : null,
      locationStatus: locationResult.status || (location.latitude == null ? "UNAVAILABLE" : "RESOLVED"),
      locationWarning: locationResult.warning || null,
      addressText: geocoding.addressText,
      geocodingStatus: geocoding.status,
      geocodingProvider: geocoding.provider,
      mapSnapshotStatus: miniMap.status,
      mapAttribution: miniMap.attribution || null,
      originalMimeType: normalized.file.type || null,
      originalByteSize: normalized.file.size,
      originalWidth: normalized.width,
      originalHeight: normalized.height,
      stampedMimeType: stamped.file.type || null,
      stampedByteSize: stamped.file.size,
      stampedWidth: stamped.width,
      stampedHeight: stamped.height,
    },
  };
}
